import React, {
  useContext,
  useEffect,
  useState,
  useRef,
  useCallback,
} from "react";
import { db } from "../../../firebase";
import {
  Box,
  Button,
  Card,
  Divider,
  CardHeader,
  Grid,
  TextField,
  Typography,
  InputAdornment,
  Switch,
  FormControlLabel,
} from "@mui/material";
import { collection, getDocs } from "firebase/firestore";
import { GoogleMap, useLoadScript, Marker } from "@react-google-maps/api";
import { useDispatch, useSelector } from "react-redux";
import {
  deletePointADireccionForCompany,
  updatePointADireccionForCompany,
  crearPointADireccionForCompany,
} from "../../../redux/accionesCompanies";
import CleaningServicesIcon from "@mui/icons-material/CleaningServices";
import CancelIcon from "@mui/icons-material/Cancel";
import AddCircleIcon from "@mui/icons-material/AddCircle";

import "@reach/combobox/styles.css";
import mapStyles from "../../../components/MapsGoogle/mapStyles";
import GooglePlacesAutocomplete from "../../../utils/objects/GooglePlacesAutocomplete";
import { UserContext } from "../../../context/CurrentUserProvider";
import FadeInOut from "../../../components/animations/FadeInOut";

const libraries = ["places", "drawing"];

const mapContainerStyle = {
  height: "50vh",
  width: "100%",
};
const options = {
  styles: mapStyles,
  disableDefaultUI: true,
  zoomControl: true,
  scrollwheel: true,
};
const center = {
  lat: -33.44488045476359,
  lng: -70.65930964013673,
};

const markerTemplate = {
  lat: -33.444307504592445,
  lng: -70.64900995751954,
};

const Direcciones = ({ currentCompany = null }) => {
  const { userDataContext } = useContext(UserContext);
  const dispatch = useDispatch();

  // Prefijo de la empresa (si aplica)
  const [prefix, setPrefix] = useState("");

  // Estado para datos de la empresa
  const [companyData, setCompanyData] = useState(null);

  // Listado de direcciones obtenidas
  const [directions, setDirections] = useState([]);
  // Banderas de edición / formulario
  const [modoEdicion, setModoEdicion] = useState(false);
  const [showEdit, setShowEdit] = useState(false);
  const [isEditNew, setIsEditNew] = useState(false);

  // Errores y mensajes de estado
  const respError = useSelector((store) => store.usuario.error);
  const [errorList, setErrorList] = useState([]);
  const [errorCritical, setErrorCritical] = useState("");
  const [success, setSuccess] = useState("");
  const [warning, setWarning] = useState("");

  // Estado del formulario de origen
  const [origenAddress, setOrigenAddress] = useState({
    geolocation: null,
    addressName: "",
    addressOriginComment: "",
    clientNameOrigin: "",
    clientRutOrigin: "",
    commerce: "",
    communeOrigin: "",
    communeParentOrigin: "",
    company: "",
    companyID: "",
    costCenter: "",
    countryOrigin: "",
    addressLine1Origin: "",
    addressLine2Origin: "",
    addressLine3Origin: "N/A",
    internalPlaceName: "",
    warehouseCode: "",
    warehouseName: "",
    regionOrigin: "",
    formattedAddressOrigin: "",
    userPhoneOrigin: "+56",
    zonePolygon: "",
  });

  // Referencia al mapa para posibles futuras operaciones
  const mapRef = useRef();

  // Estado de marcadores en el mapa
  const [markers, setMarkers] = useState([]);
  // Estado para selección de un marcador existente
  const [selected, setSelected] = useState(null);
  // Estado para arrastrar un nuevo marcador temporal
  const [newSelected, setNewSelected] = useState(null);
  // Control para saber si el campo "Departamento" está activo
  const [isDepartamento, setIsDepartamento] = useState(false);

  // Cargar prefijo y datos iniciales de empresa cuando cambien
  useEffect(() => {
    if (userDataContext.companyData) {
      setPrefix(userDataContext.companyData.prefix);
      setOrigenAddress((prev) => ({
        ...prev,
        companyID: userDataContext.companyData.companyID,
        company: userDataContext.companyData.companyName,
        commerce: userDataContext.companyData.companyName,
        clientRutOrigin: userDataContext.companyData.companyRut,
      }));
    }
  }, [userDataContext.companyData]);

  // Cuando cambie currentCompany, refetch direcciones
  useEffect(() => {
    if (currentCompany) {
      findAdressesCompany(currentCompany.companyID);
    }
  }, [currentCompany]);

  // Al cambiar userDataContext, validar empresa y obtener direcciones
  useEffect(() => {
    if (!userDataContext.companyData) {
      setErrorCritical(
        "No se encuentra empresa asociada a su cuenta. Para crear nuevas sucursales primero debe crear la Empresa o ser asociado a una cuenta comercial."
      );
    } else {
      setErrorCritical(null);
      findAdressesCompany(userDataContext.companyData.companyID);
    }
  }, [userDataContext]);

  // Función para obtener direcciones desde Firestore
  const findAdressesCompany = async (companyUid) => {
    try {
      const direccionesRef = collection(
        db,
        `${process.env.REACT_APP_COL_COMPANIES}/${companyUid}/branches`
      );
      const direccionesSnapshot = await getDocs(direccionesRef);
      const docs = direccionesSnapshot.docs.map((doc) => doc.data());
      // Filtrar las que no estén marcadas como eliminadas
      const filtered = docs.filter((item) => item.delete !== true);
      setMarkers(filtered);
      setDirections(filtered);
    } catch (e) {
      // Puedes capturar errores de lectura aquí si lo deseas
      console.error("Error al obtener direcciones:", e);
    }
  };

  // Función para eliminar una dirección
  const eliminarDireccion = (id) => {
    const dirToDelete = directions.find((item) => item.id === id);
    const remaining = directions.filter((item) => item.id !== id);
    setDirections(remaining);
    setMarkers(remaining);

    const data = {
      id,
      companyID: origenAddress.companyID || dirToDelete.companyID,
    };
    dispatch(deletePointADireccionForCompany(data));

    // Limpiar mensaje de éxito después de un segundo
    setTimeout(() => setSuccess(""), 1000);

    // Refrescar lista tras la eliminación
    findAdressesCompany(userDataContext.companyData.companyID);
  };

  // Función para cancelar modo edición / formulario
  const cancelarEdicion = () => {
    setOrigenAddress({
      geolocation: null,
      addressName: "",
      addressOriginComment: "",
      clientNameOrigin: "",
      clientRutOrigin: "",
      commerce: "",
      communeOrigin: "",
      communeParentOrigin: "",
      company: "",
      companyID: "",
      costCenter: "",
      countryOrigin: "",
      addressLine1Origin: "",
      addressLine2Origin: "",
      addressLine3Origin: "N/A",
      internalPlaceName: "",
      warehouseCode: "",
      warehouseName: "",
      regionOrigin: "",
      formattedAddressOrigin: "",
      userPhoneOrigin: "+56",
      zonePolygon: "",
    });
    setShowEdit(false);
    setIsEditNew(false);
    setModoEdicion(false);
    setErrorList([]);
    setSuccess("");
    setWarning("");
  };

  // Abrir formulario para crear nueva sucursal
  const toggleShowEditNew = () => {
    setShowEdit(true);
    setIsEditNew(true);
    setModoEdicion(false);
    setErrorList([]);
    setSuccess("");
    setWarning("");
  };

  // Cargar datos en el formulario para editar una sucursal existente
  const editarDireccion = (item) => {
    if (!item) return;
    setIsEditNew(false);
    setModoEdicion(true);

    setOrigenAddress({
      geolocation: item.geolocation || null,
      addressName: item.addressName || "",
      clientNameOrigin: item.clientNameOrigin || "",
      clientRutOrigin:
        item.clientRutOrigin || userDataContext.companyData.companyRut,
      userPhoneOrigin: item.userPhoneOrigin || "",
      formattedAddressOrigin: item.formattedAddressOrigin || "",
      addressLine1Origin: item.addressLine1Origin || "",
      addressLine2Origin: item.addressLine2Origin || "",
      addressLine3Origin: item.addressLine3Origin || "N/A",
      communeOrigin: item.communeOrigin || "",
      communeParentOrigin: item.communeParentOrigin || "",
      regionOrigin: item.regionOrigin || "",
      countryOrigin: item.countryOrigin || "",
      companyID: userDataContext.companyData.companyID,
      company: userDataContext.companyData.companyName,
      commerce: userDataContext.companyData.companyName,
      costCenter: item.costCenter || "",
      internalPlaceName: item.internalPlaceName || "",
      warehouseCode: item.warehouseCode || "",
      warehouseName: item.warehouseName || "",
      addressOriginComment: item.addressOriginComment || "",
      zonePolygon: item.zonePolygon || "",
      id: item.id, // para identificar la sucursal a editar
    });

    setErrorList([]);
    setSuccess("");
    setWarning("");
    setShowEdit(true);
  };

  // Validar y guardar (nueva o edición) dirección
  const guardarDireccion = (e) => {
    e.preventDefault();
    const errores = [];

    // Validaciones mínimas
    if (!origenAddress.clientNameOrigin.trim()) {
      errores.push("El nombre del cliente es obligatorio.");
    }
    if (!origenAddress.addressName.trim()) {
      errores.push("El nombre de la sucursal es obligatorio.");
    }
    if (
      !origenAddress.userPhoneOrigin.trim() ||
      origenAddress.userPhoneOrigin === "+56"
    ) {
      errores.push("El teléfono del usuario es obligatorio.");
    }
    if (!origenAddress.addressLine1Origin.trim()) {
      errores.push("La calle es obligatoria.");
    }
    if (!origenAddress.addressLine2Origin.trim()) {
      errores.push("El número es obligatorio.");
    }
    if (!origenAddress.addressLine3Origin.trim()) {
      errores.push("La numeración de oficina o departamento es obligatoria.");
    }
    if (!origenAddress.formattedAddressOrigin.trim()) {
      errores.push("La dirección formateada es obligatoria.");
    }
    if (!origenAddress.communeOrigin.trim()) {
      errores.push("La comuna es obligatoria.");
    }
    if (!origenAddress.regionOrigin.trim()) {
      errores.push("La región es obligatoria.");
    }
    if (!origenAddress.countryOrigin.trim()) {
      errores.push("El país es obligatorio.");
    }

    if (errores.length > 0) {
      setErrorList(errores);
      return;
    }

    setSuccess("Dirección guardada con éxito.");

    if (showEdit && isEditNew) {
      guardarDireccionUsuario();
    } else if (showEdit && !isEditNew) {
      guardarEdicionDireccionUsuario();
    }

    setErrorList([]);
  };

  // Guardar edición (callback para evitar re-creación innecesaria)
  const guardarEdicionDireccionUsuario = useCallback(async () => {
    dispatch(updatePointADireccionForCompany(origenAddress));

    setErrorList([]);
    setSuccess("Edición aplicada.");
    setWarning("");

    // Cerrar formulario en breve
    setTimeout(() => {
      setShowEdit(false);
      setSuccess("");
    }, 1000);

    // Refrescar lista
    findAdressesCompany(userDataContext.companyData.companyID);

    setIsEditNew(false);
    setModoEdicion(false);
  }, [dispatch, origenAddress, userDataContext.companyData]);

  // Guardar nueva dirección
  const guardarDireccionUsuario = useCallback(async () => {
    setErrorList([]);
    setSuccess("Creando sucursal...");

    try {
      dispatch(crearPointADireccionForCompany(origenAddress));

      setTimeout(() => {
        setShowEdit(false);
        setSuccess("");
      }, 1000);

      findAdressesCompany(userDataContext.companyData.companyID);
      setIsEditNew(false);
      setModoEdicion(false);
    } catch (e) {
      console.error("Error al crear dirección:", e);
    }
  }, [dispatch, origenAddress, userDataContext.companyData]);

  // Limpiar campos del formulario
  const limpiarFormulario = (e) => {
    e.preventDefault();
    setOrigenAddress((prev) => ({
      ...prev,
      geolocation: null,
      addressName: "",
      addressOriginComment: "",
      clientNameOrigin: "",
      clientRutOrigin: prev.clientRutOrigin, // mantener el RUT si ya venía
      commerce: "",
      communeOrigin: "",
      communeParentOrigin: "",
      company: prev.company,
      companyID: prev.companyID,
      costCenter: "",
      countryOrigin: "",
      addressLine1Origin: "",
      addressLine2Origin: "",
      addressLine3Origin: "N/A",
      internalPlaceName: "",
      warehouseCode: "",
      warehouseName: "",
      regionOrigin: "",
      formattedAddressOrigin: "",
      userPhoneOrigin: "+56",
      zonePolygon: "",
    }));
    setErrorList([]);
    setSuccess("");
    setWarning("");
  };

  // Toggle para indicar si es departamento (quita / pone "N/A" en addressLine3Origin)
  const handleChangeDireccion3 = (e) => {
    const value = e.target.checked ? "" : "N/A";
    setIsDepartamento(e.target.checked);
    setOrigenAddress((prev) => ({
      ...prev,
      addressLine3Origin: value,
    }));
  };

  // Recibir selección de Google Places
  const handleSelections = (item) => {
    setOrigenAddress((prev) => ({
      ...prev,
      formattedAddressOrigin: item.address,
      geolocation: item.addressGeoreft,
      addressLine1Origin: item.direccion1,
      addressLine2Origin: item.direccion2,
      communeOrigin: item.locality, // p.ej. comuna
      communeParentOrigin: item.commune, // p.ej. ciudad o provincia
      regionOrigin: item.region,
      countryOrigin: item.country,
    }));
  };

  // Cambios en inputs de texto
  const handleChange = (e) => {
    const { name, value } = e.target;
    setOrigenAddress((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  // Ejecutar cuando el mapa se carga
  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  return (
    <Box
      className="direction-container"
      sx={{
        display: "flex",
        width: "100%",
        height: "80vh",
        flexDirection: { xs: "column", md: "row" },
      }}
    >
      {/* PANEL DEL MAPA */}
      <Box sx={{ flex: 1, height: { xs: "50vh", md: "100%" } }}>
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          zoom={12}
          center={center}
          options={options}
          onLoad={onMapLoad}
        >
          {LoadMarket(
            selected,
            setSelected,
            setMarkers,
            newSelected,
            setNewSelected
          )}

          {markers.map((marker, index) => {
            if (marker.geolocation) {
              return (
                <Marker
                  key={index}
                  position={{
                    lat: marker.geolocation.lat,
                    lng: marker.geolocation.lng,
                  }}
                  onClick={() => {
                    setSelected(marker);
                  }}
                  onLoad={(markerInstance) => {
                    const customIcon = {
                      url: "/static/images/casita.png",
                      scaledSize: new window.google.maps.Size(50, 50),
                      anchor: new window.google.maps.Point(17.5, 17.5),
                    };
                    markerInstance.setIcon(customIcon);
                  }}
                />
              );
            }
            return null;
          })}
        </GoogleMap>
      </Box>

      {/* PANEL DE SUCURSALES / FORMULARIO */}
      <Box
        sx={{
          width: { xs: "100%", md: "30%" },
          height: { xs: "50vh", md: "100%" },
          overflowY: "auto",
          backgroundColor: "#ffffff",
          p: 2,
          borderLeft: { md: "1px solid #ccc" },
        }}
      >
        <Typography variant="h6">Sucursales</Typography>

        {directions.map((item) => (
          <Card key={item.id} sx={{ mb: 2 }}>
            <CardHeader
              title={item.addressName}
              subheader={item.formattedAddressOrigin}
            />
            <Box
              sx={{ display: "flex", justifyContent: "space-around", mb: 1 }}
            >
              <Button onClick={() => editarDireccion(item)} size="small">
                Editar
              </Button>
              <Button
                onClick={() => eliminarDireccion(item.id)}
                size="small"
                color="error"
              >
                Eliminar
              </Button>
            </Box>
          </Card>
        ))}

        <Button
          variant="contained"
          startIcon={<AddCircleIcon />}
          fullWidth
          onClick={toggleShowEditNew}
        >
          Nueva Sucursal
        </Button>

        {/* FORMULARIO / MODAL */}
        <FadeInOut show={showEdit}>
          <Box
            sx={{
              position: "absolute",
              top: 50,
              left: { xs: 0, md: "35%" },
              right: 0,
              zIndex: 3,
              backgroundColor: "white",
              maxWidth: 600,
              m: "auto",
              p: 3,
              borderRadius: 2,
              boxShadow: 5,
            }}
          >
            <Typography variant="h6">
              {modoEdicion ? "Editar Sucursal" : "Nueva Sucursal"}
            </Typography>
            <Divider sx={{ mb: 2 }} />

            {/* Mensajes de estado */}
            {success && (
              <Typography variant="body1" sx={{ color: "green", mb: 1 }}>
                {success}
              </Typography>
            )}
            {errorList.length > 0 && (
              <Box sx={{ mb: 1 }}>
                {errorList.map((err, idx) => (
                  <Typography key={idx} variant="body2" sx={{ color: "red" }}>
                    {err}
                  </Typography>
                ))}
              </Box>
            )}
            {warning && (
              <Typography variant="body1" sx={{ color: "orange", mb: 1 }}>
                {warning}
              </Typography>
            )}
            {respError && (
              <Typography variant="body1" sx={{ color: "red", mb: 1 }}>
                {respError}
              </Typography>
            )}

            <Box
              component="form"
              onSubmit={guardarDireccion}
              noValidate
              sx={{ mt: 1 }}
            >
              <Grid container spacing={2}>
                {/* Nombre del contacto */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre de Contacto"
                    name="clientNameOrigin"
                    value={origenAddress.clientNameOrigin}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <i className="fas fa-user" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Teléfono */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Teléfono"
                    name="userPhoneOrigin"
                    value={origenAddress.userPhoneOrigin}
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <i className="fas fa-phone" />
                        </InputAdornment>
                      ),
                    }}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Nombre sucursal */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Nombre de Sucursal"
                    name="addressName"
                    value={origenAddress.addressName}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Centro de Costo */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Centro de Costo"
                    name="costCenter"
                    value={origenAddress.costCenter}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Nombre referencial */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Nombre Referencial (Opcional)"
                    name="internalPlaceName"
                    value={origenAddress.internalPlaceName}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Código bodega */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Código de Bodega (Opcional)"
                    name="warehouseCode"
                    value={origenAddress.warehouseCode}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Nombre bodega */}
                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Nombre de Bodega (Opcional)"
                    name="warehouseName"
                    value={origenAddress.warehouseName}
                    onChange={handleChange}
                  />
                </Grid>

                {/* Autocomplete de dirección si aún no tiene valor */}
                {origenAddress.formattedAddressOrigin === "" ? (
                  <Grid item xs={12}>
                    <GooglePlacesAutocomplete
                      adress={origenAddress}
                      handleSelections={handleSelections}
                    />
                  </Grid>
                ) : (
                  <>
                    {/* Campo de dirección manual */}
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Dirección"
                        name="formattedAddressOrigin"
                        value={origenAddress.formattedAddressOrigin}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Switch para Departamento */}
                    <Grid item xs={12}>
                      <FormControlLabel
                        control={
                          <Switch
                            checked={isDepartamento}
                            onChange={handleChangeDireccion3}
                            name="isDepartamento"
                            color="primary"
                          />
                        }
                        label="Casa / Departamento"
                      />
                    </Grid>

                    {/* Calle */}
                    <Grid item xs={12} md={5}>
                      <TextField
                        fullWidth
                        label="Calle"
                        name="addressLine1Origin"
                        value={origenAddress.addressLine1Origin}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <i className="fas fa-map-marker-alt" />
                            </InputAdornment>
                          ),
                        }}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Número */}
                    <Grid item xs={6} md={3}>
                      <TextField
                        fullWidth
                        label="Número"
                        name="addressLine2Origin"
                        value={origenAddress.addressLine2Origin}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <i className="fas fa-hashtag" />
                            </InputAdornment>
                          ),
                        }}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Departamento */}
                    <Grid item xs={6} md={4}>
                      <TextField
                        fullWidth
                        label="Dpto"
                        name="addressLine3Origin"
                        value={origenAddress.addressLine3Origin}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <i className="fas fa-building" />
                            </InputAdornment>
                          ),
                        }}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Comuna (localidad) */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Comuna"
                        name="communeOrigin"
                        value={origenAddress.communeOrigin}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <i className="fas fa-home" />
                            </InputAdornment>
                          ),
                        }}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Ciudad / Provincia */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Ciudad / Provincia"
                        name="communeParentOrigin"
                        value={origenAddress.communeParentOrigin}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* Región */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="Región"
                        name="regionOrigin"
                        value={origenAddress.regionOrigin}
                        onChange={handleChange}
                      />
                    </Grid>

                    {/* País */}
                    <Grid item xs={12} md={6}>
                      <TextField
                        fullWidth
                        label="País"
                        name="countryOrigin"
                        value={origenAddress.countryOrigin}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <i className="fas fa-globe" />
                            </InputAdornment>
                          ),
                        }}
                        onChange={handleChange}
                      />
                    </Grid>
                  </>
                )}

                {/* Comentario opcional */}
                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Comentario (Opcional)"
                    name="addressOriginComment"
                    multiline
                    rows={3}
                    value={origenAddress.addressOriginComment}
                    onChange={handleChange}
                  />
                </Grid>
              </Grid>

              {/* Botones de acción */}
              <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
                <Button
                  type="submit"
                  variant="contained"
                  sx={{
                    backgroundColor: "#591E8F",
                    color: "#fff",
                    borderRadius: "3px",
                    padding: "8px 16px",
                    mr: 1,
                    ":hover": {
                      backgroundColor: "#25b2f4 !important",
                      color: "#591e8f",
                    },
                  }}
                >
                  Guardar
                </Button>

                <Button
                  variant="contained"
                  startIcon={<CleaningServicesIcon />}
                  onClick={limpiarFormulario}
                  sx={{
                    backgroundColor: "#db2828",
                    color: "#fff",
                    borderRadius: "3px",
                    padding: "8px 16px",
                    ":hover": {
                      opacity: 0.8,
                    },
                  }}
                >
                  Limpiar
                </Button>

                {modoEdicion && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<CancelIcon />}
                    onClick={cancelarEdicion}
                    sx={{ ml: 1 }}
                  >
                    Cancelar
                  </Button>
                )}
              </Box>
            </Box>
          </Box>
        </FadeInOut>
      </Box>
    </Box>
  );
};

// Componente auxiliar para arrastrar un nuevo marcador temporal
function LoadMarket(
  selected,
  setSelected,
  setMarkers,
  newSelected,
  setNewSelected
) {
  return newSelected ? (
    <Marker
      draggable
      position={{ lat: newSelected.lat, lng: newSelected.lng }}
      icon={{
        url: `https://images.vexels.com/media/users/3/128603/isolated/preview/c3baedd2e3624a2ed60434ab856cea3d-plantilla-de-venta-de-burbuja-roja.png`,
        origin: new window.google.maps.Point(0, 0),
        anchor: new window.google.maps.Point(15, 15),
        scaledSize: new window.google.maps.Size(50, 50),
      }}
      onDragEnd={(e) => {
        markerTemplate.lat = e.latLng.lat();
        markerTemplate.lng = e.latLng.lng();
        setNewSelected({ lat: markerTemplate.lat, lng: markerTemplate.lng });
      }}
      onClick={() => {
        setSelected({
          geolocation: { lat: markerTemplate.lat, lng: markerTemplate.lng },
        });
      }}
    />
  ) : null;
}

export default Direcciones;
